import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { 
  Database, Building2, User, Briefcase, Users, CreditCard, 
  MapPin, Calendar, Phone, Mail, Home, Pencil, Image, FileText,
  ChevronLeft, ChevronRight
} from "lucide-react";

const MARITAL_STATUSES = ["Married", "Single", "Widowed", "Divorced", "Other"];
const PAYMENT_FREQUENCIES = ["Monthly", "Quarterly", "Every Six Months", "Yearly"];
const PAYMENT_METHODS = ["Cash", "Bank Transfer", "Mobile Transfer", "Check"];

export default function AdminTenantDatabase() {
  const { token } = useAuth();
  const [properties, setProperties] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [tenantRecords, setTenantRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(false);
  
  // Edit dialog state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [tenantPhoto, setTenantPhoto] = useState(null);
  const [tenantPhotoPreview, setTenantPhotoPreview] = useState(null);
  const [tenancyAgreement, setTenancyAgreement] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Horizontal scroll for units
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    fetchProperties();
  }, [token]);

  useEffect(() => {
    if (selectedPropertyId) {
      fetchPropertyWithRecords(selectedPropertyId);
    }
  }, [selectedPropertyId]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/admin/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
      if (response.data.length > 0 && !selectedPropertyId) {
        setSelectedPropertyId(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch properties:", error);
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  const fetchPropertyWithRecords = async (propertyId) => {
    setLoadingRecords(true);
    try {
      const response = await axios.get(`${API}/admin/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedProperty(response.data);
      setTenantRecords(response.data.tenant_records || []);
    } catch (error) {
      console.error("Failed to fetch property:", error);
      toast.error("Failed to load property data");
    } finally {
      setLoadingRecords(false);
    }
  };

  const getPhotoUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const openEditDialog = (record) => {
    setSelectedRecord(record);
    setEditFormData({
      tenant_name: record.tenant_name || "",
      telephone: record.telephone || "",
      email: record.email || "",
      occupation: record.occupation || "",
      work_location: record.work_location || "",
      work_phone: record.work_phone || "",
      marital_status: record.marital_status || "",
      date_moved_in: record.date_moved_in || "",
      date_moved_out: record.date_moved_out || "",
      previous_address: record.previous_address || "",
      guarantor: {
        name: record.guarantor?.name || "",
        telephone: record.guarantor?.telephone || "",
        address: record.guarantor?.address || "",
        address_line_2: record.guarantor?.address_line_2 || ""
      },
      payment: {
        rent_payable: record.payment?.rent_payable || "",
        payment_frequency: record.payment?.payment_frequency || "",
        payment_method: record.payment?.payment_method || "",
        date_last_payment: record.payment?.date_last_payment || "",
        amount_last_payment: record.payment?.amount_last_payment || "",
        outstanding_balance: record.payment?.outstanding_balance || "",
        next_payment_due: record.payment?.next_payment_due || ""
      }
    });
    if (record.tenant_photo_url) {
      setTenantPhotoPreview(getPhotoUrl(record.tenant_photo_url));
    } else {
      setTenantPhotoPreview(null);
    }
    setTenantPhoto(null);
    setTenancyAgreement(null);
    setShowEditDialog(true);
  };

  const handleTenantPhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTenantPhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setTenantPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleTenancyAgreementChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTenancyAgreement(file);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = new FormData();
      
      // Add basic fields
      Object.keys(editFormData).forEach(key => {
        if (key !== "guarantor" && key !== "payment") {
          data.append(key, editFormData[key] || "");
        }
      });
      
      // Add guarantor and payment as JSON
      data.append("guarantor_json", JSON.stringify(editFormData.guarantor));
      data.append("payment_json", JSON.stringify(editFormData.payment));
      
      // Add files if selected
      if (tenantPhoto) {
        data.append("tenant_photo", tenantPhoto);
      }
      if (tenancyAgreement) {
        data.append("tenancy_agreement", tenancyAgreement);
      }

      await axios.put(`${API}/admin/tenant-record/${selectedRecord.id}`, data, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.success("Tenant record updated successfully");
      setShowEditDialog(false);
      fetchPropertyWithRecords(selectedPropertyId);
    } catch (error) {
      console.error("Failed to update tenant record:", error);
      toast.error(error.response?.data?.detail || "Failed to update tenant record");
    } finally {
      setSubmitting(false);
    }
  };

  const scrollUnits = (direction) => {
    const container = document.getElementById("units-container");
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({ 
        left: direction === "left" ? -scrollAmount : scrollAmount, 
        behavior: "smooth" 
      });
    }
  };

  // Get tenant record for a specific unit
  const getRecordForUnit = (unitNumber) => {
    return tenantRecords.find(r => r.unit_number === unitNumber) || null;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-background">
        <Sidebar />
        <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
          <div className="flex justify-center py-12">
            <div className="animate-pulse text-primary">Loading...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-tenant-database-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-full mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Database className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-primary">Tenant Database</h1>
                <p className="text-text-muted">View and manage tenant records by property</p>
              </div>
            </div>
          </div>

          {/* Property Selector */}
          {properties.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Building2 className="w-16 h-16 text-stone-300 mb-4" />
                <h3 className="text-lg font-medium text-stone-600 mb-2">No Properties Yet</h3>
                <p className="text-text-muted text-center">
                  Add properties first to manage tenant records
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="mb-6">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <Label className="text-sm text-stone-500 mb-1 block">Select Property</Label>
                      <Select value={selectedPropertyId} onValueChange={setSelectedPropertyId}>
                        <SelectTrigger className="w-full md:w-[400px]" data-testid="property-selector">
                          <SelectValue placeholder="Select a property" />
                        </SelectTrigger>
                        <SelectContent>
                          {properties.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} - {p.location_address}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {loadingRecords ? (
                <div className="flex justify-center py-12">
                  <div className="animate-pulse text-primary">Loading property data...</div>
                </div>
              ) : selectedProperty ? (
                <Card>
                  {/* Property Header */}
                  <CardHeader className="border-b bg-amber-50">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      {/* Property Info Block */}
                      <div className="flex-shrink-0 bg-white rounded-xl p-4 border border-amber-200 min-w-[280px]">
                        <div className="space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <span className="font-medium text-stone-500 w-32">House Location:</span>
                            <span className="text-stone-700">{selectedProperty.location_address}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-medium text-stone-500 w-32">Type of Construction:</span>
                            <span className="text-stone-700">{selectedProperty.type_of_construction}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-medium text-stone-500 w-32">Number of Units:</span>
                            <span className="text-stone-700">{selectedProperty.number_of_units}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-medium text-stone-500 w-32">Date Completed:</span>
                            <span className="text-stone-700">{selectedProperty.date_completed || "N/A"}</span>
                          </div>
                        </div>
                        
                        {/* Property Photo */}
                        {selectedProperty.photo_url && (
                          <div className="mt-3 aspect-video rounded-lg overflow-hidden bg-stone-100">
                            <img 
                              src={getPhotoUrl(selectedProperty.photo_url)} 
                              alt={selectedProperty.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                      </div>

                      {/* Property Title */}
                      <div className="flex-1 text-center lg:text-left">
                        <h2 className="text-2xl md:text-3xl font-bold text-primary">
                          {selectedProperty.name}
                        </h2>
                        <Badge className="mt-2 bg-primary/10 text-primary">
                          {selectedProperty.number_of_units} Units
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="p-0">
                    {/* Scroll Controls */}
                    <div className="flex items-center justify-between p-2 bg-stone-50 border-b">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => scrollUnits("left")}
                        className="text-stone-500"
                      >
                        <ChevronLeft className="w-5 h-5" />
                        <span className="hidden sm:inline">Scroll Left</span>
                      </Button>
                      <span className="text-sm text-stone-500">
                        Scroll to view all units →
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => scrollUnits("right")}
                        className="text-stone-500"
                      >
                        <span className="hidden sm:inline">Scroll Right</span>
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </div>

                    {/* Units Grid - Horizontal Scroll */}
                    <div 
                      id="units-container"
                      className="overflow-x-auto"
                      style={{ scrollbarWidth: "thin" }}
                    >
                      <div className="flex min-w-max">
                        {/* Row Labels Column */}
                        <div className="flex-shrink-0 w-48 bg-stone-50 border-r sticky left-0 z-10">
                          {/* Unit Header */}
                          <div className="h-16 flex items-center px-3 font-semibold text-primary border-b bg-stone-100">
                            Unit Information
                          </div>
                          
                          {/* Tenant Info Section */}
                          <div className="bg-green-50 px-3 py-1 text-xs font-semibold text-green-800 border-b">
                            Tenant's Information
                          </div>
                          {[
                            "Tenant Name: Last/First",
                            "Telephone #",
                            "Email Address",
                            "Occupation",
                            "Work Location",
                            "Work Phone #",
                            "Marital Status",
                            "Date Moved In",
                            "Date Moved Out",
                            "Tenancy Agreement",
                            "Previous Address",
                            "Photo"
                          ].map((label, i) => (
                            <div key={i} className="h-10 flex items-center px-3 text-sm text-stone-600 border-b bg-white">
                              {label}
                            </div>
                          ))}

                          {/* Guarantor Section */}
                          <div className="bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-800 border-b">
                            Guarantor's Information
                          </div>
                          {[
                            "Name: Last/First",
                            "Telephone #",
                            "Address",
                            "Address Line 2"
                          ].map((label, i) => (
                            <div key={i} className="h-10 flex items-center px-3 text-sm text-stone-600 border-b bg-white">
                              {label}
                            </div>
                          ))}

                          {/* Payment Section */}
                          <div className="bg-yellow-50 px-3 py-1 text-xs font-semibold text-yellow-800 border-b">
                            Payment Information
                          </div>
                          {[
                            "Rent Payable",
                            "Freq of Payment",
                            "Method of Payment",
                            "Date of Last Payment",
                            "Amount of Last Payment",
                            "Outstanding Balance",
                            "Next Payment Due"
                          ].map((label, i) => (
                            <div key={i} className="h-10 flex items-center px-3 text-sm text-stone-600 border-b bg-white">
                              {label}
                            </div>
                          ))}

                          {/* Actions Row */}
                          <div className="h-12 flex items-center px-3 text-sm font-medium text-primary border-b bg-stone-100">
                            Actions
                          </div>
                        </div>

                        {/* Unit Columns */}
                        {(selectedProperty.units || []).map((unit, unitIndex) => {
                          const record = getRecordForUnit(unit.unit_number);
                          return (
                            <div 
                              key={unit.unit_number} 
                              className="flex-shrink-0 w-52 border-r"
                              data-testid={`unit-column-${unit.unit_number}`}
                            >
                              {/* Unit Header */}
                              <div className="h-16 flex flex-col items-center justify-center px-2 text-center border-b bg-red-50">
                                <span className="font-semibold text-primary text-sm">
                                  UNIT {unit.unit_number}
                                </span>
                                <span className="text-xs text-stone-500">
                                  ({unit.unit_type})
                                </span>
                              </div>

                              {/* Tenant Info Section */}
                              <div className="bg-green-50 px-3 py-1 text-xs text-green-800 border-b text-center">
                                Tenant Information
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-green-50/30 truncate">
                                {record?.tenant_name || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-white truncate">
                                {record?.telephone || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-green-50/30 truncate">
                                {record?.email || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-white truncate">
                                {record?.occupation || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-green-50/30 truncate">
                                {record?.work_location || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-white truncate">
                                {record?.work_phone || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-green-50/30 truncate">
                                {record?.marital_status || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-white truncate">
                                {record?.date_moved_in || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-green-50/30 truncate">
                                {record?.date_moved_out || "-"}
                              </div>
                              <div className="h-10 flex items-center justify-center px-2 text-sm border-b bg-white">
                                {record?.tenancy_agreement_url ? (
                                  <a 
                                    href={getPhotoUrl(record.tenancy_agreement_url)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline text-xs"
                                  >
                                    View File
                                  </a>
                                ) : "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-green-50/30 truncate">
                                {record?.previous_address || "-"}
                              </div>
                              <div className="h-10 flex items-center justify-center px-2 border-b bg-amber-50">
                                {record?.tenant_photo_url ? (
                                  <div className="w-8 h-8 rounded-full overflow-hidden bg-stone-200">
                                    <img 
                                      src={getPhotoUrl(record.tenant_photo_url)} 
                                      alt="Tenant"
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center">
                                    <User className="w-4 h-4 text-stone-400" />
                                  </div>
                                )}
                              </div>

                              {/* Guarantor Section */}
                              <div className="bg-blue-50 px-3 py-1 text-xs text-blue-800 border-b text-center">
                                &nbsp;
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-blue-50/30 truncate">
                                {record?.guarantor?.name || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-white truncate">
                                {record?.guarantor?.telephone || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-blue-50/30 truncate">
                                {record?.guarantor?.address || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-white truncate">
                                {record?.guarantor?.address_line_2 || "-"}
                              </div>

                              {/* Payment Section */}
                              <div className="bg-yellow-50 px-3 py-1 text-xs text-yellow-800 border-b text-center">
                                &nbsp;
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-yellow-50/30 truncate">
                                {record?.payment?.rent_payable ? `₦${record.payment.rent_payable}` : "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-white truncate">
                                {record?.payment?.payment_frequency || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-yellow-50/30 truncate">
                                {record?.payment?.payment_method || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-white truncate">
                                {record?.payment?.date_last_payment || "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-yellow-50/30 truncate">
                                {record?.payment?.amount_last_payment ? `₦${record.payment.amount_last_payment}` : "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-white truncate">
                                {record?.payment?.outstanding_balance ? `₦${record.payment.outstanding_balance}` : "-"}
                              </div>
                              <div className="h-10 flex items-center px-2 text-sm border-b bg-yellow-50/30 truncate">
                                {record?.payment?.next_payment_due || "-"}
                              </div>

                              {/* Actions Row */}
                              <div className="h-12 flex items-center justify-center px-2 border-b bg-stone-100">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => record && openEditDialog(record)}
                                  className="text-xs"
                                  data-testid={`edit-unit-${unit.unit_number}-btn`}
                                >
                                  <Pencil className="w-3 h-3 mr-1" />
                                  Edit
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : null}
            </>
          )}
        </div>
      </main>

      {/* Edit Tenant Record Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              Edit Tenant Record - Unit {selectedRecord?.unit_number}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmitEdit}>
            <Tabs defaultValue="tenant" className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="tenant" className="text-xs sm:text-sm">
                  <User className="w-4 h-4 mr-1 hidden sm:inline" />
                  Tenant Info
                </TabsTrigger>
                <TabsTrigger value="guarantor" className="text-xs sm:text-sm">
                  <Users className="w-4 h-4 mr-1 hidden sm:inline" />
                  Guarantor
                </TabsTrigger>
                <TabsTrigger value="payment" className="text-xs sm:text-sm">
                  <CreditCard className="w-4 h-4 mr-1 hidden sm:inline" />
                  Payment
                </TabsTrigger>
              </TabsList>

              {/* Tenant Info Tab */}
              <TabsContent value="tenant" className="space-y-4">
                {/* Photo Upload */}
                <div className="flex items-center gap-4 p-4 bg-stone-50 rounded-xl">
                  <div className="w-20 h-20 rounded-full bg-stone-200 flex items-center justify-center overflow-hidden">
                    {tenantPhotoPreview ? (
                      <img src={tenantPhotoPreview} alt="Tenant" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-10 h-10 text-stone-400" />
                    )}
                  </div>
                  <div>
                    <Label className="text-sm">Tenant Photo</Label>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleTenantPhotoChange}
                      className="mt-1 max-w-[200px]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tenant Name (Last/First)</Label>
                    <Input
                      placeholder="e.g., Okesola Tunde"
                      value={editFormData.tenant_name}
                      onChange={(e) => setEditFormData({ ...editFormData, tenant_name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telephone #</Label>
                    <Input
                      placeholder="e.g., +234 801 234 5678"
                      value={editFormData.telephone}
                      onChange={(e) => setEditFormData({ ...editFormData, telephone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={editFormData.email}
                      onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Occupation</Label>
                    <Input
                      placeholder="e.g., Teacher, Engineer"
                      value={editFormData.occupation}
                      onChange={(e) => setEditFormData({ ...editFormData, occupation: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Work Location</Label>
                    <Input
                      placeholder="e.g., Lagos, Ibadan"
                      value={editFormData.work_location}
                      onChange={(e) => setEditFormData({ ...editFormData, work_location: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Work Phone #</Label>
                    <Input
                      placeholder="Work phone number"
                      value={editFormData.work_phone}
                      onChange={(e) => setEditFormData({ ...editFormData, work_phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Marital Status</Label>
                    <Select 
                      value={editFormData.marital_status} 
                      onValueChange={(v) => setEditFormData({ ...editFormData, marital_status: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {MARITAL_STATUSES.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Date Moved In</Label>
                    <Input
                      type="date"
                      value={editFormData.date_moved_in}
                      onChange={(e) => setEditFormData({ ...editFormData, date_moved_in: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date Moved Out</Label>
                    <Input
                      type="date"
                      value={editFormData.date_moved_out}
                      onChange={(e) => setEditFormData({ ...editFormData, date_moved_out: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Previous Address</Label>
                    <Input
                      placeholder="Previous address before moving here"
                      value={editFormData.previous_address}
                      onChange={(e) => setEditFormData({ ...editFormData, previous_address: e.target.value })}
                    />
                  </div>
                </div>

                {/* Tenancy Agreement Upload */}
                <div className="p-4 bg-blue-50 rounded-xl">
                  <Label className="text-sm">Copy of Tenancy Agreement</Label>
                  <div className="flex items-center gap-4 mt-2">
                    {selectedRecord?.tenancy_agreement_url && (
                      <a 
                        href={getPhotoUrl(selectedRecord.tenancy_agreement_url)} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                      >
                        <FileText className="w-4 h-4" />
                        View Current File
                      </a>
                    )}
                    <Input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={handleTenancyAgreementChange}
                      className="max-w-[250px]"
                    />
                  </div>
                  {tenancyAgreement && (
                    <p className="text-xs text-green-600 mt-1">New file selected: {tenancyAgreement.name}</p>
                  )}
                </div>
              </TabsContent>

              {/* Guarantor Tab */}
              <TabsContent value="guarantor" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Guarantor Name (Last/First)</Label>
                    <Input
                      placeholder="e.g., Adeyemi Johnson"
                      value={editFormData.guarantor?.name || ""}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        guarantor: { ...editFormData.guarantor, name: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Telephone #</Label>
                    <Input
                      placeholder="Guarantor's phone number"
                      value={editFormData.guarantor?.telephone || ""}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        guarantor: { ...editFormData.guarantor, telephone: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Address</Label>
                    <Input
                      placeholder="Guarantor's address"
                      value={editFormData.guarantor?.address || ""}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        guarantor: { ...editFormData.guarantor, address: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Address Line 2</Label>
                    <Input
                      placeholder="Additional address info"
                      value={editFormData.guarantor?.address_line_2 || ""}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        guarantor: { ...editFormData.guarantor, address_line_2: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Payment Tab */}
              <TabsContent value="payment" className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Rent Payable (₦)</Label>
                    <Input
                      placeholder="e.g., 62,500"
                      value={editFormData.payment?.rent_payable || ""}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        payment: { ...editFormData.payment, rent_payable: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Frequency of Payment</Label>
                    <Select 
                      value={editFormData.payment?.payment_frequency || ""} 
                      onValueChange={(v) => setEditFormData({ 
                        ...editFormData, 
                        payment: { ...editFormData.payment, payment_frequency: v }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_FREQUENCIES.map(f => (
                          <SelectItem key={f} value={f}>{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Method of Payment</Label>
                    <Select 
                      value={editFormData.payment?.payment_method || ""} 
                      onValueChange={(v) => setEditFormData({ 
                        ...editFormData, 
                        payment: { ...editFormData.payment, payment_method: v }
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        {PAYMENT_METHODS.map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Date of Last Payment</Label>
                    <Input
                      type="date"
                      value={editFormData.payment?.date_last_payment || ""}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        payment: { ...editFormData.payment, date_last_payment: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Amount of Last Payment (₦)</Label>
                    <Input
                      placeholder="e.g., 62,500"
                      value={editFormData.payment?.amount_last_payment || ""}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        payment: { ...editFormData.payment, amount_last_payment: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Outstanding Balance (₦)</Label>
                    <Input
                      placeholder="e.g., 0"
                      value={editFormData.payment?.outstanding_balance || ""}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        payment: { ...editFormData.payment, outstanding_balance: e.target.value }
                      })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Next Payment Due</Label>
                    <Input
                      type="date"
                      value={editFormData.payment?.next_payment_due || ""}
                      onChange={(e) => setEditFormData({ 
                        ...editFormData, 
                        payment: { ...editFormData.payment, next_payment_due: e.target.value }
                      })}
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-primary hover:bg-primary-800" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
